import { api } from './api';

/**
 * Tipos del payload del backend GET /api/despacho/registros.
 * El grouping es Cliente → Operación (faena) → Pedido → Registros.
 */
export interface DespachoRegistro {
  id: number;
  estado: string;
  cantidad: number;
  receta_id: number;
  receta_nombre: string;
  tipo_plato: string | null;
  /** Fecha de la última transición a entregado_a_despacho (ISO). */
  fecha_a_despacho: string | null;
  /** Fecha de entrega al cliente (ISO). */
  fecha_entrega_cliente: string | null;
  /** Quien entrega del lado de la empresa (capturado al entregar). */
  entregado_por: string | null;
  /** Quien recibe del lado del cliente. */
  recibido_por: string | null;
  /** Comentario asociado a la entrega al cliente. */
  comentario_entrega: string | null;
  /** Segmento del detalle origen (Fase 5, 2026-06-19). null en
   *  registros pre-flag (la mayoría hoy). */
  segmento: 'dia' | 'noche' | 'transversal' | null;
  /** true cuando la receta está marcada es_insumo=1
   *  (pan/cubiertos/agua/empaque). */
  es_insumo: boolean;
}

export interface DespachoTandaBucket {
  estado: string | null;
  registros: DespachoRegistro[];
}

export interface DespachoPedido {
  pedido_id: number;
  pedido_codigo: string;
  fecha_entrega: string;
  registros: DespachoRegistro[];
  /** Fase 6: presente cuando por_segmento=true (default en el service). */
  segmentos?: {
    dia: DespachoTandaBucket;
    noche: DespachoTandaBucket;
    transversal: DespachoTandaBucket;
  };
}

export interface DespachoOperacion {
  operacion_id: number;
  operacion_nombre: string;
  direccion: string | null;
  comuna: string | null;
  ciudad: string | null;
  pedidos: DespachoPedido[];
}

export interface DespachoCliente {
  cliente_id: number;
  cliente_nombre: string;
  observaciones_despacho: string | null;
  operaciones: DespachoOperacion[];
}

export type ModoDespacho = 'vigente' | 'entregados' | 'todos';

export interface ListarParams {
  modo?: ModoDespacho;
  fecha_desde?: string;
  fecha_hasta?: string;
  search?: string;
  cliente_ids?: number[];
  operacion_ids?: number[];
  /** @deprecated usar modo='todos' */
  incluir_entregados?: boolean;
  /** @deprecated usar fecha_desde/fecha_hasta */
  fecha?: string;
}

export const despachoService = {
  async listar(params: ListarParams = {}): Promise<DespachoCliente[]> {
    const query: Record<string, string> = {};
    if (params.modo) query.modo = params.modo;
    if (params.fecha_desde) query.fecha_desde = params.fecha_desde;
    if (params.fecha_hasta) query.fecha_hasta = params.fecha_hasta;
    if (params.search) query.search = params.search;
    if (params.cliente_ids?.length)
      query.cliente_ids = params.cliente_ids.join(',');
    if (params.operacion_ids?.length)
      query.operacion_ids = params.operacion_ids.join(',');
    if (params.incluir_entregados) query.incluir_entregados = 'true';
    if (params.fecha) query.fecha = params.fecha;
    // Fase 6 (2026-07-01): siempre pide agrupación por segmento; los
    // consumidores viejos ignoran el campo extra `segmentos` y siguen
    // usando `registros` como antes.
    query.por_segmento = 'true';
    const r = await api.get('/despacho/registros', { params: query });
    return r.data?.data || [];
  },

  /**
   * Fase 6: entregar todos los registros de una tanda (día/noche/transversal)
   * al cliente en un solo POST. Reusa el endpoint `/despacho/pedido/:id/tanda`.
   * Payload opcional con firmas (entregado_por / recibido_por / comentario)
   * — quedan persistidos en cocina_registro_historial_estado para cada uno
   * de los registros de esa tanda, y aparecen como firmas en el comprobante
   * segmentado.
   */
  async entregarTanda(
    pedidoId: number,
    segmento: 'dia' | 'noche' | 'transversal',
    payload: {
      comentario?: string;
      entregado_por?: string;
      recibido_por?: string;
    } = {}
  ): Promise<void> {
    const clean = (v?: string) => {
      const t = (v ?? '').trim();
      return t.length > 0 ? t : undefined;
    };
    await api.post(`/despacho/pedido/${pedidoId}/tanda/${segmento}/entregar`, {
      entregado_por: clean(payload.entregado_por),
      recibido_por: clean(payload.recibido_por),
      comentario: clean(payload.comentario),
    });
  },

  async entregarAlCliente(
    registroId: number,
    payload: {
      comentario?: string;
      entregado_por?: string;
      recibido_por?: string;
    } = {}
  ): Promise<void> {
    await api.post(`/despacho/${registroId}/transicion`, {
      estado_nuevo: 'entregado_al_cliente',
      comentario:
        payload.comentario && payload.comentario.trim()
          ? payload.comentario.trim()
          : null,
      entregado_por:
        payload.entregado_por && payload.entregado_por.trim()
          ? payload.entregado_por.trim()
          : null,
      recibido_por:
        payload.recibido_por && payload.recibido_por.trim()
          ? payload.recibido_por.trim()
          : null,
    });
  },

  async devolverACocina(registroId: number): Promise<void> {
    // entregado_a_despacho → terminado (reversa)
    await api.post(`/cocina/${registroId}/revertir`);
  },
};
